// LOGOMAN PRO — Web Audio button sound effects.
// 8-bit-flavoured square-wave blips synthesised on the fly. Zero asset files.
// AudioContext is lazy-created on first user gesture (browser autoplay policy).

const STORAGE_KEY = "lp-sounds-muted";
const MASTER_VOLUME = 0.15;

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;

let muted = (() => {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
})();

function ensureContext(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return null;
    ctx = new Ctx();
    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : MASTER_VOLUME;
    masterGain.connect(ctx.destination);
  } catch {
    return null;
  }
  return ctx;
}

function blip(freq: number, type: OscillatorType, duration: number, peak: number) {
  const c = ensureContext();
  if (!c || !masterGain) return;
  if (c.state === "suspended") void c.resume();
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, c.currentTime);
  g.gain.setValueAtTime(0.0001, c.currentTime);
  g.gain.exponentialRampToValueAtTime(peak, c.currentTime + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + duration);
  osc.connect(g);
  g.connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + duration + 0.02);
}

/** Light UI tick — generic click. */
export function tick() {
  blip(880, "square", 0.06, 0.5);
}

/** Confirmation arpeggio — primary actions (Save, Start designing, Use template). */
export function confirm() {
  const c = ensureContext();
  if (!c || !masterGain) return;
  if (c.state === "suspended") void c.resume();
  const notes = [523.25, 659.25, 783.99]; // C5 — E5 — G5
  const noteDur = 0.07;
  notes.forEach((freq, i) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    const t0 = c.currentTime + i * noteDur;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.5, t0 + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + noteDur - 0.004);
    osc.connect(g);
    g.connect(masterGain!);
    osc.start(t0);
    osc.stop(t0 + noteDur + 0.02);
  });
}

/** Descending swoosh — navigation transitions. */
export function swoosh() {
  const c = ensureContext();
  if (!c || !masterGain) return;
  if (c.state === "suspended") void c.resume();
  const osc = c.createOscillator();
  const g = c.createGain();
  const filt = c.createBiquadFilter();
  filt.type = "lowpass";
  filt.frequency.value = 1500;
  osc.type = "square";
  osc.frequency.setValueAtTime(620, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(220, c.currentTime + 0.09);
  g.gain.setValueAtTime(0.0001, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.4, c.currentTime + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.1);
  osc.connect(filt);
  filt.connect(g);
  g.connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + 0.12);
}

export function setMuted(m: boolean) {
  muted = m;
  if (masterGain) masterGain.gain.value = m ? 0 : MASTER_VOLUME;
  try {
    localStorage.setItem(STORAGE_KEY, m ? "1" : "0");
  } catch {
    // localStorage not available — ignore
  }
}

export function isMuted(): boolean {
  return muted;
}
